"use client";

import { useEffect, useState } from "react";
import { isVersionSkewError, recoverFromVersionSkew } from "@/lib/version-skew";

/**
 * Frontière d'erreur de dernier recours : elle couvre le layout racine, que
 * `error.tsx` ne protège pas — et c'est précisément là que tombe une erreur de
 * décalage de versions, puisque le layout est le premier à monter.
 *
 * Elle remplace le layout racine quand elle s'affiche : styles et balises
 * `html`/`body` sont donc à sa charge, d'où le style en ligne plutôt que les
 * classes utilitaires, qui pourraient venir du morceau de code défaillant.
 */
export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    if (isVersionSkewError(error) && recoverFromVersionSkew()) {
      setRecovering(true);
      return;
    }
    console.error(error);
  }, [error]);

  return (
    <html lang="fr">
      <body
        style={{
          margin: 0,
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          background: "#ffffff",
          color: "#111827",
        }}
      >
        <title>Fast</title>
        {recovering ? (
          <p style={{ fontSize: 14, color: "#6b7280" }}>Mise à jour de l&apos;application…</p>
        ) : (
          <div style={{ maxWidth: 360, padding: 24, textAlign: "center" }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>
              Un problème est survenu
            </h1>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>
              Réessayez, ou rechargez la page.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                onClick={() => unstable_retry()}
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: "10px 20px",
                  fontSize: 14,
                  fontWeight: 700,
                  background: "#111827",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
              >
                Réessayer
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  padding: "10px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  background: "transparent",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                Recharger
              </button>
            </div>
          </div>
        )}
      </body>
    </html>
  );
}
